import { useState, useCallback, useRef } from "react";
import { SpotlightProvider } from "@mantine/spotlight";
import { IconSearch } from "@tabler/icons-react";
import { Loader } from "@mantine/core";
import { useLocation } from "react-router-dom";
import { searchMeetingSummaries } from "api";
import useWorkspaceMeetingNavigator from "hooks/useWorkspaceMeetingNavigator";

const SpotlightSearchProvider = ({ children }) => {
  const location = useLocation();
  const selectWorkspaceMeeting = useWorkspaceMeetingNavigator();
  const [actions, setActions] = useState([]);
  const searchTimeoutRef = useRef(null);

  const isWorkspacePage = location.pathname === "/workspace";

  // Extract first line from text
  const getFirstLine = useCallback((text) => {
    if (!text) return "";
    const firstLine = text.split("\n")[0];
    return firstLine.length > 100 ? firstLine.substring(0, 100) + "..." : firstLine;
  }, []);

  // Get description from meeting data
  const getDescription = useCallback((meeting) => {
    // Try to get first discussion text
    if (meeting.discussions && meeting.discussions.length > 0) {
      const firstDiscussion = meeting.discussions[0];
      if (typeof firstDiscussion === "string") {
        return getFirstLine(firstDiscussion);
      } else if (firstDiscussion.text) {
        return getFirstLine(firstDiscussion.text);
      }
    }

    // Fallback to key takeaways
    if (meeting.keyTakeaways && meeting.keyTakeaways.length > 0) {
      const firstTakeaway = meeting.keyTakeaways[0];
      if (typeof firstTakeaway === "string") {
        return getFirstLine(firstTakeaway);
      } else if (firstTakeaway.text) {
        return getFirstLine(firstTakeaway.text);
      }
    }

    // Fallback to tasks assigned
    if (meeting.tasksAssigned && meeting.tasksAssigned.length > 0) {
      const firstTask = meeting.tasksAssigned[0];
      if (typeof firstTask === "string") {
        return getFirstLine(firstTask);
      } else if (firstTask.text) {
        return getFirstLine(firstTask.text);
      }
    }

    return "No summary available";
  }, [getFirstLine]);

  // Format date for display
  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  // Perform search with debouncing
  const performSearch = useCallback(
    (searchQuery) => {
      // Only search on workspace page
      if (!isWorkspacePage) {
        setActions([]);
        return;
      }

      // Clear any pending search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Show loading state immediately
      setActions([
        {
          id: "loading-1",
          title: "Searching...",
          description: "Loading meetings",
          icon: <Loader size="sm" />,
          onTrigger: () => {},
        },
      ]);

      // If query is empty or too short, load recent meetings
      const queryToSearch = searchQuery && searchQuery.trim().length >= 2
        ? searchQuery
        : "*";

      // Debounce the search
      searchTimeoutRef.current = setTimeout(() => {
        searchMeetingSummaries(queryToSearch, 1, 20)
          .then((response) => {
            const newActions = response.results.map((meeting) => ({
              id: meeting.meetingId,
              title: meeting.meetingTitle || "Untitled Meeting",
              description: `${formatDate(meeting.meetingDate)} - ${getDescription(meeting)}`,
              onTrigger: () => selectWorkspaceMeeting(meeting),
            }));

            setActions(newActions);
          })
          .catch((error) => {
            console.error("Error searching meetings:", error);
            setActions([
              {
                id: "error",
                title: "Error searching meetings",
                description: error.message || "Please try again",
                onTrigger: () => {},
              },
            ]);
          });
      }, 300); // 300ms debounce
    },
    [isWorkspacePage, selectWorkspaceMeeting, getDescription, formatDate]
  );

  // Handle query changes
  const handleQueryChange = useCallback(
    (newQuery) => {
      performSearch(newQuery);
    },
    [performSearch]
  );

  return (
    <SpotlightProvider
      actions={actions}
      searchIcon={<IconSearch size={18} />}
      searchPlaceholder="Search meeting summaries..."
      shortcut={["mod + K"]}
      nothingFoundMessage="No meetings found"
      highlightQuery
      limit={20}
      onQueryChange={handleQueryChange}
      filter={(_query, actions) => {
        // Don't filter - let the API handle it
        return actions;
      }}
    >
      {children}
    </SpotlightProvider>
  );
};

export default SpotlightSearchProvider;
